import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { appeler, ErreurApi } from "../api/client";
import { fcfa, useApi } from "../api/hooks";
import { EtatChargement, EtatErreur } from "../composants/Avis";
import { Tableau } from "../composants/Tableau";
import { ChampMontant } from "../composants/ChampMontant";

const NatureSchema = z.object({
  cle: z.string(),
  libelle: z.string(),
  domaine: z.string(),
  arbitrage: z.string(),
  motifAttente: z.string().nullable(),
  alternative: z.string().nullable(),
});

const LigneSchema = z.object({
  id: z.string(),
  date: z.string(),
  numeroPiece: z.string().nullable(),
  libelle: z.string(),
  nature: z.string(),
  natureLibre: z.string().nullable(),
  tiers: z.string().nullable(),
  sens: z.string(),
  montant: z.number(),
  modeReglement: z.string().nullable(),
  pieceScannee: z.string(),
  pieceRangee: z.boolean(),
  dossierDuJustificatif: z.string().nullable(),
  justificatifId: z.string().nullable(),
  validePar: z.string().nullable(),
  montantConfirme: z.boolean(),
  montantInvraisemblable: z.boolean(),
});

const AjoutSchema = z.object({
  id: z.string(),
  montantInvraisemblable: z.boolean(),
  dossierDeDepot: z.string(),
});

const MODES = ["Espèces", "Virement", "Chèque", "Mobile money", "Carte"] as const;

/**
 * §5.B — Journal général du portail client.
 * Saisie simplifiée à 10 colonnes, dont 8 saisies.
 */
export function JournalClient({ dossierId }: { dossierId: string }) {
  const qc = useQueryClient();

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [numeroPiece, setNumeroPiece] = useState("");
  const [libelle, setLibelle] = useState("");
  const [nature, setNature] = useState("vente-marchandises");
  const [natureLibre, setNatureLibre] = useState("");
  const [tiers, setTiers] = useState("");
  const [sens, setSens] = useState<"Entree" | "Sortie">("Entree");
  const [montant, setMontant] = useState("");
  const [mode, setMode] = useState("Virement");
  const [erreur, setErreur] = useState<string | null>(null);
  const [depot, setDepot] = useState<string | null>(null);

  const natures = useApi(["natures"], "/dossiers/x/journal/natures", z.array(NatureSchema));
  const lignes = useApi(["journal-client", dossierId], `/dossiers/${dossierId}/journal`, z.array(LigneSchema));

  const natureChoisie = natures.data?.find((n) => n.cle === nature);

  async function enregistrer(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);

    const valeur = Number(montant.replace(/\s/g, "").replace(",", "."));
    if (!Number.isFinite(valeur) || valeur <= 0) {
      setErreur("Le montant doit être un nombre supérieur à zéro.");
      return;
    }

    try {
      const r = await appeler(`/dossiers/${dossierId}/journal`, AjoutSchema, {
        method: "POST",
        body: JSON.stringify({
          date,
          numeroPiece: numeroPiece || null,
          libelle,
          nature,
          natureLibre: nature === "autre" ? natureLibre : null,
          tiers: tiers || null,
          sens,
          montant: valeur,
          modeReglement: mode,
        }),
      });

      setDepot(r.dossierDeDepot);
      setLibelle("");
      setNumeroPiece("");
      setMontant("");
      setTiers("");
      void qc.invalidateQueries({ queryKey: ["journal-client", dossierId] });
    } catch (err) {
      setErreur(err instanceof ErreurApi ? (err.motif ?? err.message) : "Enregistrement impossible.");
    }
  }

  async function confirmerMontant(id: string) {
    await appeler(
      `/dossiers/${dossierId}/journal/${id}/confirmer-montant`,
      z.object({ montantConfirme: z.boolean() }),
      { method: "POST" },
    );
    void qc.invalidateQueries({ queryKey: ["journal-client", dossierId] });
  }

  return (
    <div>
      <section>
        <h3>Nouvelle écriture</h3>

        <form onSubmit={enregistrer} className="saisie-journal">
          <label>
            Date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </label>

          <label>
            N° pièce
            <input value={numeroPiece} onChange={(e) => setNumeroPiece(e.target.value)} />
          </label>

          <label className="large">
            Libellé
            <input value={libelle} onChange={(e) => setLibelle(e.target.value)} required />
          </label>

          <label>
            Nature
            <select value={nature} onChange={(e) => setNature(e.target.value)}>
              {natures.data?.map((n) => (
                <option key={n.cle} value={n.cle}>{n.libelle}</option>
              ))}
            </select>
          </label>

          {nature === "autre" && (
            <label className="large">
              Précisez
              <input
                value={natureLibre}
                onChange={(e) => setNatureLibre(e.target.value)}
                placeholder="Nature de l'opération"
              />
            </label>
          )}

          <label>
            Tiers
            <input value={tiers} onChange={(e) => setTiers(e.target.value)} />
          </label>

          <label>
            Sens
            <select value={sens} onChange={(e) => setSens(e.target.value as "Entree" | "Sortie")}>
              <option value="Entree">Entrée</option>
              <option value="Sortie">Sortie</option>
            </select>
          </label>

          <div className="large">
            {/* ⛔ Montant en `type=text`, jamais `number` : les flèches du spinner
                incrémentaient à 1 au moindre clic. */}
            <ChampMontant
              label="Montant"
              valeur={montant}
              onChange={setMontant}
              confirme={false}
              onConfirmer={() => {}}
            />
          </div>

          <label>
            Mode de règlement
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              {MODES.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>

          <button type="submit">Enregistrer</button>
        </form>

        {natureChoisie?.arbitrage === "Attente" && (
          <div className="alerte alerte--info">
            <strong>Cette nature demandera un arbitrage.</strong>
            <p>{natureChoisie.motifAttente}</p>
            {natureChoisie.alternative && <p>{natureChoisie.alternative}</p>}
          </div>
        )}

        {erreur && <p className="alerte" role="alert">{erreur}</p>}

        {depot && (
          <p className="depot">
            Déposez le justificatif dans : <strong>{depot}</strong>
          </p>
        )}
      </section>

      <section>
        <h3>Écritures saisies</h3>

        {lignes.isLoading && <EtatChargement quoi="du journal" />}
        {lignes.error && <EtatErreur erreur={lignes.error} />}

        <Tableau
          colonnes={[
            { cle: "d", titre: "Date", rendu: (l) => l.date },
            { cle: "p", titre: "N° pièce", rendu: (l) => l.numeroPiece ?? "—" },
            { cle: "l", titre: "Libellé", rendu: (l) => l.libelle },
            { cle: "n", titre: "Nature", rendu: (l) => l.natureLibre ?? l.nature },
            { cle: "t", titre: "Tiers", rendu: (l) => l.tiers ?? "—" },
            { cle: "s", titre: "Sens", rendu: (l) => (l.sens === "Entree" ? "Entrée" : "Sortie") },
            {
              cle: "m",
              titre: "Montant",
              montant: true,
              rendu: (l) =>
                l.montantInvraisemblable ? (
                  <span className="montant-suspect">
                    {fcfa(l.montant)}
                    <button type="button" onClick={() => void confirmerMontant(l.id)}>
                      C'est bien le montant
                    </button>
                  </span>
                ) : (
                  fcfa(l.montant)
                ),
            },
            { cle: "mo", titre: "Mode", rendu: (l) => l.modeReglement ?? "—" },
            {
              cle: "sc",
              // Colonnes NON MODIFIABLES par le client : c'est un constat.
              titre: "Scanné",
              rendu: (l) => (
                <span className={l.pieceScannee === "oui" ? "oui" : "non"}>{l.pieceScannee}</span>
              ),
            },
            {
              cle: "ra",
              titre: "Rangé",
              rendu: (l) =>
                l.pieceRangee ? (
                  <span className="oui" title={l.dossierDuJustificatif ?? ""}>
                    {l.dossierDuJustificatif?.split("/").slice(-2).join(" / ") ?? "oui"}
                  </span>
                ) : (
                  <span className="non">non</span>
                ),
            },
          ]}
          lignes={lignes.data ?? []}
          cle={(l) => l.id}
          vide="Aucune écriture saisie."
        />

        <p className="note">
          Les colonnes « Scanné » et « Rangé » sont un <strong>constat</strong>,
          pas une déclaration : « rangé » est vérifié sur le stockage. Elles ne
          sont pas modifiables.
        </p>
      </section>
    </div>
  );
}
